import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { validateRequired } from '@/utils/cadastroValidation';

const CadastroForm = ({ fields, initialData, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      const defaultData = {};
      fields.forEach(f => { defaultData[f.name] = ''; });
      setFormData(defaultData);
    }
  }, [initialData, fields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    let hasErrors = false;

    fields.forEach(field => {
      if (field.required) {
        const error = validateRequired(formData[field.name], field.label);
        if (error) {
          newErrors[field.name] = error;
          hasErrors = true;
        }
      }
      if (field.validate && formData[field.name]) {
        const error = field.validate(formData[field.name]);
        if (error) {
          newErrors[field.name] = error;
          hasErrors = true;
        }
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {fields.map(field => (
        <div key={field.name} className="space-y-2">
          <Label htmlFor={field.name}>{field.label} {field.required && '*'}</Label>
          
          {field.type === 'textarea' ? (
            <Textarea
              id={field.name}
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              placeholder={`Digite ${field.label.toLowerCase()}`}
              className="text-slate-900"
            />
          ) : field.type === 'select' ? (
            <Select 
              value={formData[field.name] || ''} 
              onValueChange={(val) => handleSelectChange(field.name, val)}
            >
              <SelectTrigger className="text-slate-900">
                <SelectValue placeholder={`Selecione ${field.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id={field.name}
              name={field.name}
              type={field.type || 'text'}
              value={formData[field.name] || ''}
              onChange={handleChange}
              placeholder={`Digite ${field.label.toLowerCase()}`}
              className="text-slate-900"
            />
          )}
          {errors[field.name] && <p className="text-sm text-red-500">{errors[field.name]}</p>}
        </div>
      ))}
      
      <div className="flex justify-end space-x-2 pt-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading} className="text-slate-700">
            Cancelar
          </Button>
        )}
        <Button type="submit" disabled={loading} className="bg-[#193D6D] text-white hover:bg-[#193D6D]/90">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {initialData ? 'Atualizar' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
};

export default CadastroForm;